import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  LinearProgress,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CommuteIcon from '@mui/icons-material/Commute';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TuneIcon from '@mui/icons-material/Tune';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import axios from '../../api/axiosInstance';
import ClientTimeAnalytics from './ClientTimeAnalytics';
import ClientActivityJournal from './ClientActivityJournal';

function formatHours(value) {
  return `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}h`;
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString('fr-FR') : '-';
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('fr-FR') : '-';
}

function formatDateOnly(value) {
  if (!value) return '-';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return formatDate(value);
}

function getInitials(value) {
  const label = String(value || '').trim();
  if (!label) return '?';
  return label.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase();
}

function profileIcon(profile) {
  const Icon = { admin: AdminPanelSettingsIcon, engineer: EngineeringIcon, pilotage: ManageAccountsIcon }[profile];
  return Icon ? <Icon fontSize="small" /> : undefined;
}

function normalizeProfile(value) {
  const profile = String(value || '').trim().toLowerCase();
  if (profile === 'ingé' || profile === 'inge' || profile === 'ingénieur' || profile === 'ingenieur') return 'engineer';
  return profile;
}

function profileLabel(value) {
  return { admin: 'Admin', engineer: 'Ingé', pilotage: 'Pilotage' }[normalizeProfile(value)] || value || 'Non affecté';
}

function formatDelta(value) {
  const number = Number(value || 0);
  return `${number > 0 ? '+' : ''}${formatHours(number)}`;
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function formatCount(value) {
  return Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

function parseDateInput(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function toDateInputValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getArticleCycle(article = {}) {
  return article.serviceMetric === 'travel' ? 'annuel' : (article.billingCycle || 'oneshot');
}

function getArticleLineTotal(article = {}) {
  return Number(article.quantity || 0) * Number(article.unitPrice || 0);
}

function getArticleMonthlyTotal(article = {}) {
  if (Number.isFinite(Number(article.monthlyTotal))) return Number(article.monthlyTotal);
  const lineTotal = getArticleLineTotal(article);
  return getArticleCycle(article) === 'annuel' ? lineTotal / 12 : lineTotal;
}

function getArticleAnnualTotal(article = {}) {
  if (Number.isFinite(Number(article.annualTotal))) return Number(article.annualTotal);
  const lineTotal = getArticleLineTotal(article);
  return getArticleCycle(article) === 'mensuel' ? lineTotal * 12 : lineTotal;
}

function getContractArticlesMonthlyTotal(contract) {
  return (contract?.articles || []).reduce((sum, article) => sum + getArticleMonthlyTotal(article), 0);
}

function getContractArticlesAnnualTotal(contract) {
  return (contract?.articles || []).reduce((sum, article) => sum + getArticleAnnualTotal(article), 0);
}

function getContractMetricRate(contract, metric) {
  const articles = (contract?.articles || []).filter((article) => article.serviceMetric === metric);
  if (!articles.length) return 0;
  const totalQuantity = articles.reduce((sum, article) => sum + Number(article.quantity || 0), 0);
  const totalValue = articles.reduce((sum, article) => sum + Number(article.quantity || 0) * Number(article.unitPrice || 0), 0);
  const rate = totalQuantity > 0 ? totalValue / totalQuantity : 0;
  return metric.endsWith('_days') ? rate / 7 : rate;
}

function getContractFinancialDelta(contract, data, metric) {
  const delta = Number(data?.delta?.[metric] || 0);
  const sourceMetric = {
    admin: 'admin_hours',
    engineer: 'engineer_hours',
    pilotage: 'pilotage_days',
    travel: 'travel',
  }[metric] || metric;
  return delta * getContractMetricRate(contract, sourceMetric);
}

function formatSignedCurrency(value) {
  const amount = Number(value || 0);
  return `${amount > 0 ? '+' : ''}${formatCurrency(amount)}`;
}

function metricLabel(metric) {
  return {
    admin_hours: 'Heures admin',
    engineer_hours: 'Heures ingé',
    admin_days: 'Jours admin',
    engineer_days: 'Jours ingé',
    pilotage_days: 'Jours pilotage',
    travel: 'Déplacement',
  }[metric] || metric || 'Article';
}

function matrixProfileLabel(value) {
  return profileLabel(value);
}

function getMatrixRows(contract) {
  const items = Array.isArray(contract?.matrix?.competences) ? contract.matrix.competences : [];
  return items.map((item, index) => ({
    id: item._id || item.sourceMatrixItemId || `${item.competence || item.category || 'competence'}-${index}`,
    category: item.category || '-',
    competence1: item.competence || item.easyRedmineSkill || item.category || 'Non renseigné',
    competence2: item.detail || item.easyRedmineLevel || item.matrixLevel || '-',
    profile: matrixProfileLabel(item.profile),
    includedProfiles: item.includedProfiles || {},
    included: item.included === true,
    active: item.active !== false,
  }));
}


const INFOGERANCE_METRICS = new Set(['admin_hours', 'engineer_hours', 'admin_days', 'engineer_days', 'pilotage_days', 'travel']);

function normalizeText(value = '') {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function isInfogeranceArticle(article = {}) {
  if (INFOGERANCE_METRICS.has(article.serviceMetric)) return true;
  const text = normalizeText([
    article.productName,
    article.productCode,
    article.category,
    article.subCategory,
  ].filter(Boolean).join(' '));
  return text.includes('infogerance');
}

function getArticleSectionTotals(articles = []) {
  return articles.reduce((acc, article) => {
    acc.monthly += getArticleMonthlyTotal(article);
    acc.annual += getArticleAnnualTotal(article);
    return acc;
  }, { monthly: 0, annual: 0 });
}

function ContractArticlesSection({ title, articles }) {
  const totals = getArticleSectionTotals(articles);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ p: 1.75 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{articles.length} article(s)</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" color="primary" variant="outlined" label={`${formatCurrency(totals.monthly)} / mois`} />
          <Chip size="small" variant="outlined" label={`${formatCurrency(totals.annual)} / an`} />
        </Stack>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Article</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Cycle</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>Qté</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>Prix unit.</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>Total mensuel</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>Total annuel</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articles.map((article, index) => {
              const quantity = Number(article.quantity || 0);
              const unitPrice = Number(article.unitPrice || 0);
              const monthlyTotal = getArticleMonthlyTotal(article);
              const annualTotal = getArticleAnnualTotal(article);
              return (
                <TableRow key={article._id || `${title}-${index}`} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{article.productName || '-'}</Typography>
                    {(article.productCode || article.category || article.subCategory) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {[article.productCode, article.category, article.subCategory].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{metricLabel(article.serviceMetric)}</TableCell>
                  <TableCell>{getArticleCycle(article)}</TableCell>
                  <TableCell align="right">{quantity.toLocaleString('fr-FR')}</TableCell>
                  <TableCell align="right">{formatCurrency(unitPrice)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(monthlyTotal)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(annualTotal)}</TableCell>
                </TableRow>
              );
            })}
            {!articles.length && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>Aucun article.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function ContractMetricCard({ label, helper, icon, data, metricKey, color, onClick, selected }) {
  const used = Number(data?.used?.[metricKey] || 0);
  const included = Number(data?.included?.[metricKey] || 0);
  const delta = Number(data?.delta?.[metricKey] || 0);
  const remaining = Math.max(0, included - used);
  const overrun = delta > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.25,
        borderRadius: 2,
        height: '100%',
        borderColor: overrun ? 'warning.main' : 'divider',
        bgcolor: overrun ? 'rgba(245, 158, 11, 0.06)' : 'background.paper',
        boxShadow: 2,
        cursor: onClick ? 'pointer' : 'default',
        ...(selected ? { borderWidth: 2, borderColor: color } : {}),
      }}
      onClick={onClick}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color,
                bgcolor: `${color}14`,
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{label}</Typography>
              <Typography variant="caption" color="text.secondary">{helper}</Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            color={overrun ? 'warning' : 'success'}
            label={overrun ? `${formatHours(delta)} à régul` : `${formatHours(remaining)} dispo`}
          />
        </Stack>

        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
            {formatHours(used)}
            <Typography component="span" variant="h6" color="text.secondary" sx={{ fontWeight: 700 }}> / {formatHours(included)}</Typography>
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function TravelMetricCard({ data, onClick, selected }) {
  const used = Number(data?.used?.travel || 0);
  const included = Number(data?.included?.travel || 0);
  const delta = Number(data?.delta?.travel || 0);
  const remaining = Math.max(0, included - used);
  const overrun = delta > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.25,
        borderRadius: 2,
        height: '100%',
        borderColor: overrun ? 'warning.main' : 'divider',
        bgcolor: overrun ? 'rgba(245, 158, 11, 0.06)' : 'background.paper',
        boxShadow: 2,
        cursor: onClick ? 'pointer' : 'default',
        ...(selected ? { borderWidth: 2, borderColor: '#f59e0b' } : {}),
      }}
      onClick={onClick}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b', bgcolor: '#f59e0b14' }}>
              <CommuteIcon />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Déplacements</Typography>
              <Typography variant="caption" color="text.secondary">Déplacements Helpdesk infogérance</Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            color={overrun ? 'warning' : 'success'}
            label={overrun ? `${formatCount(delta)} à régul` : `${formatCount(remaining)} dispo`}
          />
        </Stack>

        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
            {formatCount(used)}
            <Typography component="span" variant="h6" color="text.secondary" sx={{ fontWeight: 700 }}> / {formatCount(included)}</Typography>
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function ClientContracts({ initialTab = 'contracts' }) {
  const [data, setData] = useState(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedPeriod, setSelectedPeriod] = useState('currentYear');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [selectedContractId, setSelectedContractId] = useState('');
  const [trackerFilter, setTrackerFilter] = useState('all');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [consumptionFilter, setConsumptionFilter] = useState('all');
  const [consumptionSearch, setConsumptionSearch] = useState('');
  const [expandedConsumptionTickets, setExpandedConsumptionTickets] = useState(() => new Set());
  const [tableTrackerFilter, setTableTrackerFilter] = useState('all');
  const [tableProfileFilter, setTableProfileFilter] = useState('all');
  const [viewingContract, setViewingContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams({
      year: String(selectedYear),
      contractId: selectedContractId,
      view: 'contracts',
      periodMode: selectedPeriod,
    });
    if (trackerFilter !== 'all') params.set('trackerMode', trackerFilter);
    if (selectedPeriod === 'custom' && customStartDate && customEndDate) {
      params.set('startDate', customStartDate);
      params.set('endDate', customEndDate);
    }
    axios.get(`/api/client/dashboard?${params.toString()}`)
      .then((response) => {
        if (!active) return;
        setData(response.data);
        setError('');
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || 'Impossible de charger les contrats.');
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [customEndDate, customStartDate, selectedContractId, selectedPeriod, selectedYear, trackerFilter]);

  const regularization = data?.regularization;
  const contracts = regularization?.byContract || [];
  const selectedContract = contracts.find((contract) => String(contract._id) === String(selectedContractId));
  useEffect(() => {
    if (selectedPeriod !== 'currentYear' || !selectedContract?.startDate) return;
    const start = new Date(selectedContract.startDate);
    if (Number.isNaN(start.getTime())) return;
    const now = new Date();
    const anniversaryReached = now.getMonth() > start.getMonth()
      || (now.getMonth() === start.getMonth() && now.getDate() >= start.getDate());
    const currentContractYear = now.getFullYear() + (anniversaryReached ? 1 : 0);
    if (selectedYear !== currentContractYear) setSelectedYear(currentContractYear);
  }, [selectedContract, selectedPeriod, selectedYear]);
  const periodData = selectedContract || null;
  const contractPeriod = useMemo(() => {
    if (selectedPeriod === 'month') return null;
    if (selectedPeriod === 'custom') {
      const start = parseDateInput(customStartDate);
      const end = parseDateInput(customEndDate);
      return start && end && start <= end ? { start, end } : null;
    }
    if (selectedContract?.startDate) {
      const contractStart = new Date(selectedContract.startDate);
      if (!Number.isNaN(contractStart.getTime())) {
        // Une période contractuelle annuelle s'arrête la veille de l'anniversaire.
        // Ex.: 01/09/2025 -> 31/08/2026, jamais 01/09/2026.
        const periodStart = new Date(selectedYear - 1, contractStart.getMonth(), contractStart.getDate());
        const periodEnd = new Date(selectedYear, contractStart.getMonth(), contractStart.getDate());
        periodEnd.setDate(periodEnd.getDate() - 1);
        periodEnd.setHours(23, 59, 59, 999);

        const actualContractStart = new Date(selectedContract.startDate);
        const actualContractEnd = selectedContract.endDate ? new Date(selectedContract.endDate) : null;
        const start = actualContractStart > periodStart ? actualContractStart : periodStart;
        const end = actualContractEnd && actualContractEnd < periodEnd ? actualContractEnd : periodEnd;
        return start <= end ? { start, end } : { start: periodStart, end: periodEnd };
      }
    }
    if (data?.period?.selectedYearStart && data?.period?.selectedYearEnd) {
      const start = new Date(data.period.selectedYearStart);
      const end = new Date(data.period.selectedYearEnd);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) return { start, end };
    }
    return null;
  }, [customEndDate, customStartDate, data, selectedContract, selectedPeriod, selectedYear]);

  useEffect(() => {
    if (selectedPeriod !== 'custom' || customStartDate || customEndDate) return;
    if (selectedContract?.startDate) {
      const start = new Date(selectedContract.startDate);
      if (!Number.isNaN(start.getTime())) {
        const from = new Date(selectedYear - 1, start.getMonth(), start.getDate());
        const to = new Date(selectedYear, start.getMonth(), start.getDate());
        to.setDate(to.getDate() - 1);
        setCustomStartDate(toDateInputValue(from));
        setCustomEndDate(toDateInputValue(to));
        return;
      }
    }
    const now = new Date();
    setCustomStartDate(toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)));
    setCustomEndDate(toDateInputValue(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
  }, [customEndDate, customStartDate, selectedContract, selectedPeriod, selectedYear]);

  useEffect(() => {
    setDraftStartDate(customStartDate);
    setDraftEndDate(customEndDate);
  }, [customStartDate, customEndDate]);

  const formatPeriodDate = (value) => value.toLocaleDateString('fr-FR');
  const activePeriodKey = selectedPeriod === 'month' ? 'month' : 'year';
  const activePeriodLabel = contractPeriod
    ? 'Du ' + formatPeriodDate(contractPeriod.start) + ' au ' + formatPeriodDate(contractPeriod.end)
    : selectedPeriod === 'month'
      ? (() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return `${formatPeriodDate(start)} au ${formatPeriodDate(end)}`;
      })()
      : (selectedPeriod === 'currentYear' ? 'Période en cours' : 'Année ' + (regularization?.yearLabel || selectedYear));
  const activeIncludedKey = selectedPeriod === 'month' ? 'monthIncluded' : 'yearIncluded';
  const activeDeltaKey = selectedPeriod === 'month' ? 'month' : 'year';
  const consumptions = useMemo(() => data?.consumptions?.[activePeriodKey] || [], [activePeriodKey, data]);
  const trackerOptions = useMemo(() => [...new Set(consumptions.map((row) => row.tracker).filter(Boolean))].sort(), [consumptions]);
  const profileOptions = useMemo(() => [...new Set(consumptions.map((row) => row.profile).filter(Boolean))].sort(), [consumptions]);
  const filteredConsumptions = useMemo(() => {
    const query = consumptionSearch.trim().toLocaleLowerCase('fr-FR');
    return consumptions.filter((row) => {
      const matchesCard = consumptionFilter === 'all'
        || (consumptionFilter === 'travel' ? row.travel > 0 : row.profile === consumptionFilter);
      const matchesTracker = tableTrackerFilter === 'all' || row.tracker === tableTrackerFilter;
      const matchesProfile = tableProfileFilter === 'all' || row.profile === tableProfileFilter;
      const searchable = [row.user, row.tracker, row.activity, row.comments, row.ticketId]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('fr-FR');
      return matchesCard && matchesTracker && matchesProfile && (!query || searchable.includes(query));
    });
  }, [consumptionFilter, consumptionSearch, consumptions, tableProfileFilter, tableTrackerFilter]);
  const consumptionRows = useMemo(() => filteredConsumptions.map((row, index) => ({
    ...row,
    id: row._id || `${row.ticketId || 'consumption'}-${row.spentOn || index}-${index}`,
  })), [filteredConsumptions]);
  const groupedConsumptionRows = useMemo(() => {
    const groups = new Map();
    consumptionRows.forEach((row) => {
      const key = row.ticketId || row.ticketSubject || 'Sans ticket';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return [...groups.entries()].map(([ticket, rows]) => ({ ticket, rows, total: rows.reduce((sum, row) => sum + Number(row.hours || 0), 0) }));
  }, [consumptionRows]);
  const consumptionColumns = useMemo(() => [
    {
      field: 'spentOn',
      headerName: 'Date',
      minWidth: 115,
      flex: 0.8,
      renderCell: ({ value }) => value ? new Date(value).toLocaleDateString('fr-FR') : '-',
    },
    {
      field: 'ticketId',
      headerName: 'Ticket',
      minWidth: 105,
      flex: 0.75,
      renderCell: ({ value }) => value ? `#${value}` : '-',
    },
    { field: 'user', headerName: 'Intervenant', minWidth: 145, flex: 1 },
    { field: 'tracker', headerName: 'Tracker', minWidth: 105, flex: 0.8 },
    { field: 'activity', headerName: 'Activité', minWidth: 135, flex: 1 },
    {
      field: 'profile',
      headerName: 'Niveau',
      minWidth: 105,
      flex: 0.8,
      valueGetter: (value) => ({ admin: 'Admin', engineer: 'Ingé', pilotage: 'Pilotage' }[value] || value || '-'),
    },
    {
      field: 'hours',
      headerName: 'Heures',
      minWidth: 95,
      flex: 0.65,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ value }) => <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatHours(value)}</Typography>,
    },
    {
      field: 'travel',
      headerName: 'Déplacement',
      minWidth: 115,
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ value }) => value ? 'Oui' : '-',
    },
    { field: 'comments', headerName: 'Commentaire', minWidth: 260, flex: 2.2 },
  ], []);
  const periodOptionLabel = (year) => {
    if (!selectedContract?.startDate) return 'Année ' + year;
    const start = new Date(selectedContract.startDate);
    if (Number.isNaN(start.getTime())) return 'Année ' + year;
    const from = new Date(year - 1, start.getMonth(), start.getDate());
    const to = new Date(year, start.getMonth(), start.getDate());
    to.setDate(to.getDate() - 1);
    return from.toLocaleDateString('fr-FR') + ' au ' + to.toLocaleDateString('fr-FR');
  };

  const yearOptions = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
  const contractYearOptions = useMemo(() => {
    if (!selectedContract) return [currentYear];
    const start = selectedContract.startDate ? new Date(selectedContract.startDate).getFullYear() : currentYear - 3;
    const end = selectedContract.endDate ? new Date(selectedContract.endDate).getFullYear() : currentYear;
    const from = Number.isFinite(start) ? start : currentYear - 3;
    const to = Number.isFinite(end) ? Math.max(end, currentYear) : currentYear;
    return Array.from({ length: Math.max(1, to - from + 1) }, (_, index) => from + index);
  }, [currentYear, selectedContract]);
  const rows = selectedContract ? [selectedContract] : [];
  const matrixRows = useMemo(() => getMatrixRows(selectedContract), [selectedContract]);

  if (loading && !data) {
    return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>;
  }

  if (error && !data) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <>
    {loading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: (theme) => theme.zIndex.appBar + 1 }} />}
      <Stack gap={2} sx={{ position: 'relative' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ minHeight: { xs: 'auto', md: 118 } }}>
        <Box sx={{ py: 0.5 }}>
          <Typography variant="subtitle1" sx={{ color: 'success.dark', fontWeight: 900, lineHeight: 1.2 }}>{data?.organization?.name || 'Client'}</Typography>
          <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1.15 }}>Rapport d'activité</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', md: 'auto' }, alignContent:'center' }}>
          <FormControl size="small" sx={{ minWidth: 180, order: 2, display: 'none' }}>
            <InputLabel id="client-contract-period-label">Périodess</InputLabel>
            <Select
              labelId="client-contract-period-label"
              value={selectedPeriod}
              label="Période"
              onChange={(event) => setSelectedPeriod(event.target.value)}
            >
              <MenuItem value="currentYear">Périodes en cours</MenuItem>
              <MenuItem value="year">Par périodes</MenuItem>
              <MenuItem value="month">Mois en courss</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150, order: 3, display: 'none' }}>
            <InputLabel id="client-contract-year-label">Périodes</InputLabel>
            <Select
              labelId="client-contract-year-label"
              value={selectedYear}
              label="Période"
              onChange={(event) => setSelectedYear(Number(event.target.value))}
            >
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>{periodOptionLabel(year)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: { xs: '100%', md: 820 }, order: 1, display: 'none' }}>
            <InputLabel id="client-contract-header-label">Contrat</InputLabel>
            <Select
              labelId="client-contract-header-label"
              value={selectedContractId}
              label="Contrat"
              onChange={(event) => setSelectedContractId(event.target.value)}
              renderValue={() => selectedContract ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={900} noWrap>{selectedContract.reference || 'Contrat'}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{selectedContract.name || 'Contrat de service'}</Typography>
                  </Box>
                  <Chip size="small" color="success" variant="outlined" label={`${formatCurrency(getContractArticlesMonthlyTotal(selectedContract))} / mois`} />
                  <Chip size="small" variant="outlined" label={`${formatCurrency(getContractArticlesAnnualTotal(selectedContract))} / an`} />
                </Stack>
              ) : 'Sélectionner un contrat'}
              sx={{
                minHeight: 112,
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 2 },
              }}
            >
              <MenuItem value="">Sélectionner un contrat</MenuItem>
              {contracts.map((contract) => (
                <MenuItem key={contract._id} value={contract._id}>
                  <Stack><Typography variant="subtitle2" fontWeight={800}>{contract.reference || 'Contrat'}</Typography><Typography variant="caption">{contract.name || 'Contrat de service'}</Typography></Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch" sx={{
        width: { xs: '100%', md: '72%' }, my: { xs: 1.5, md: 0 }, p: { xs: 1.25, md: 1.5 }, borderRadius: 3,
        position: { xs: 'static', md: 'absolute' }, top: { md: 0 }, right: { md: 0 },
        border: '1px solid rgba(15, 118, 110, 0.18)',
        background: 'linear-gradient(115deg, rgba(255,255,255,.92) 0%, rgba(236,252,203,.78) 24%, rgba(209,250,229,.72) 52%, rgba(167,243,208,.55) 78%, rgba(220,252,231,.68) 100%)',
        boxShadow: '0 12px 30px rgba(15, 118, 110, .1)', backdropFilter: 'blur(14px)',
      }}>
      <FormControl size="small" fullWidth sx={{ flex: 1 }}>
            <InputLabel id="client-contract-filter-label">Sélectionnez le contrat</InputLabel>
            <Select
              labelId="client-contract-filter-label"
              value={selectedContractId}
              label="Sélectionnez le contrat"
              onChange={(event) => setSelectedContractId(event.target.value)}
              renderValue={() => selectedContract ? (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 0.5 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={900} noWrap sx={{ color: '#173b2b' }}>
                      {selectedContract.name || 'Contrat de service'}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ color: '#35624d' }}>
                      {selectedContract.reference || 'Sans référence'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.75}>
                    <Chip size="small" color="success" variant="outlined" label={`${formatCurrency(getContractArticlesMonthlyTotal(selectedContract))} / mois`} />
                    <Chip size="small" variant="outlined" label={`${formatCurrency(getContractArticlesAnnualTotal(selectedContract))} / an`} />
                  </Stack>
                </Stack>
              ) : 'Sélectionner un contrat'}
              sx={{
                minHeight: 72, borderRadius: 2.5, bgcolor: 'rgba(187,247,208,.28)', color: '#173b2b',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(15,118,110,.28)' },
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.75 },
              }}
            >
              <MenuItem value="" sx={{ minHeight: 64 }}>Sélectionner un contrat</MenuItem>
              {contracts.map((contract) => (
                <MenuItem key={contract._id} value={contract._id} sx={{ minHeight: 78 }}>
                  <Stack spacing={0.25} sx={{ width: '100%' }}>
                    <Typography variant="subtitle2" fontWeight={800}>{contract.name || 'Contrat de service'}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary">{contract.reference || 'Sans référence'}</Typography>
                      <Chip size="small" variant="outlined" label={`${formatCurrency(getContractArticlesMonthlyTotal(contract))} / mois`} />
                      <Chip size="small" variant="outlined" label={`${formatCurrency(getContractArticlesAnnualTotal(contract))} / an`} />
                    </Stack>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
      </FormControl>
      {selectedContract && (
        <Stack direction="column" spacing={1} alignItems="stretch" justifyContent="center">
          <Button variant="contained" color="success" startIcon={<VisibilityIcon />} onClick={() => setViewingContract(selectedContract)}>
            Voir le contrat
          </Button>
          <Button variant="contained" color="info" startIcon={<DownloadIcon />} disabled>
            Télécharger
          </Button>
        </Stack>
      )}
      </Stack>

      {selectedContract && contractPeriod && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: 0, mb: 0.5, px: 0.5, lineHeight: 1.4 }}>
          Période du contrat : {formatPeriodDate(contractPeriod.start)} au {formatPeriodDate(contractPeriod.end)}
        </Typography>
      )}

      <Paper variant="outlined" sx={{
        p: { xs: 1.5, md: 2 }, mt: { xs: 2, md: 3 }, order: 3, display: selectedContract ? 'block' : 'none', borderRadius: 3, borderColor: 'rgba(52, 211, 153, 0.28)', position: 'relative',
        background: 'linear-gradient(120deg, rgba(18, 91, 64, .9) 0%, rgba(24, 118, 71, .88) 38%, rgba(45, 145, 76, .82) 68%, rgba(101, 145, 30, .76) 100%)',
        boxShadow: '0 14px 36px rgba(5, 45, 32, 0.18)', backdropFilter: 'blur(16px)', color: '#ecfdf5',
        '& .MuiInputLabel-root': { color: 'rgba(236,253,245,.72)' },
        '& .MuiInputLabel-root.Mui-focused': { color: '#6ee7b7' },
        '& .MuiOutlinedInput-root': { color: '#f0fdf4', bgcolor: 'rgba(255,255,255,.07)', borderRadius: 2, '& fieldset': { borderColor: 'rgba(167,243,208,.28)' }, '&:hover fieldset': { borderColor: '#6ee7b7' }, '&.Mui-focused fieldset': { borderColor: '#34d399' } },
        '& .MuiSelect-icon': { color: '#a7f3d0' },
        '& .MuiToggleButton-root': { color: 'rgba(236,253,245,.78)', borderColor: 'rgba(167,243,208,.25)', bgcolor: 'rgba(255,255,255,.04)', '&.Mui-selected': { color: '#052e1d', bgcolor: '#6ee7b7', fontWeight: 800 }, '&:hover': { bgcolor: 'rgba(110,231,183,.18)' } },
      }}>
        <Typography variant="overline" sx={{ position: { xs: 'static', md: 'absolute' }, left: { md: 18 }, top: { md: '50%' }, transform: { md: 'translateY(-50%)' }, display: 'flex', alignItems: 'center', gap: 0.5, mb: { xs: 1, md: 0 }, px: 0.5, color: '#ecfccb', fontWeight: 900, letterSpacing: '0.12em' }}><TuneIcon sx={{ fontSize: 16 }} />Filtres</Typography>
        <Stack sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '120px minmax(0, 1fr)' }, columnGap: { xs: 0, lg: 1.5 }, rowGap: 1.25, alignItems: 'center', pl: { xs: 0, lg: 16 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 36 }}>
            <CalendarMonthOutlinedIcon fontSize="small" sx={{ mr: 0.5, color: '#f0fdf4' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1.2, color: '#f0fdf4' }}>Période</Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={selectedPeriod}
            onChange={(_, value) => {
              if (value === null) return;
              setSelectedPeriod(value);
              if (value === 'currentYear') setSelectedYear(currentYear);
            }}
            sx={{ gridColumn: { xs: '1', lg: '2' }, flexWrap: 'nowrap', overflowX: 'auto' }}
          >
            <ToggleButton value="month">Mois en cours</ToggleButton>
            <ToggleButton value="currentYear">Période en cours</ToggleButton>
            <ToggleButton value="year">Par période</ToggleButton>
            <ToggleButton value="custom">Personnalisée</ToggleButton>
          </ToggleButtonGroup>
          {selectedPeriod === 'year' && (
            <FormControl size="small" sx={{ minWidth: 280, gridColumn: { xs: '1', lg: '2' } }}>
              <InputLabel id="client-contract-year-period-label">Période contractuelle</InputLabel>
              <Select
                labelId="client-contract-year-period-label"
                value={selectedYear}
                label="Période contractuelle"
                onChange={(event) => setSelectedYear(Number(event.target.value))}
              >
                {contractYearOptions.map((year) => (
                  <MenuItem key={year} value={year}>{year} - {periodOptionLabel(year)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {selectedPeriod === 'custom' && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ gridColumn: { xs: '1', lg: '2' }, flexWrap: 'wrap' }}>
              <DatePicker
                label="Date de début"
                value={draftStartDate ? dayjs(draftStartDate) : null}
                maxDate={draftEndDate ? dayjs(draftEndDate) : undefined}
                onChange={(value) => setDraftStartDate(value?.isValid() ? value.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: 'small', sx: { borderRadius: 1, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#fff !important' }, '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff !important' }, '& .MuiInput-underline:before, & .MuiInput-underline:after': { borderBottomColor: '#fff !important' }, '& .MuiInputBase-input': { color: '#111827' }, '& .MuiInputLabel-root': { color: '#374151' }, '& .MuiSvgIcon-root': { color: '#374151' } } },
                  field: { clearable: true },
                }}
                sx={{ width: { xs: '100%', sm: 240 }, minWidth: { sm: 240 } }}
              />
              <DatePicker
                label="Date de fin"
                value={draftEndDate ? dayjs(draftEndDate) : null}
                minDate={draftStartDate ? dayjs(draftStartDate) : undefined}
                onChange={(value) => setDraftEndDate(value?.isValid() ? value.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: 'small', sx: { borderRadius: 1, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#fff !important' }, '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff !important' }, '& .MuiInput-underline:before, & .MuiInput-underline:after': { borderBottomColor: '#fff !important' }, '& .MuiInputBase-input': { color: '#111827' }, '& .MuiInputLabel-root': { color: '#374151' }, '& .MuiSvgIcon-root': { color: '#374151' } } },
                  field: { clearable: true },
                }}
                sx={{ width: { xs: '100%', sm: 240 }, minWidth: { sm: 240 } }}
              />
              <Button
                variant="contained"
                size="medium"
                disabled={!draftStartDate || !draftEndDate || draftStartDate > draftEndDate}
                onClick={() => { setCustomStartDate(draftStartDate); setCustomEndDate(draftEndDate); }}
                sx={{ minWidth: 112, minHeight: 40, fontWeight: 800 }}
              >
                Appliquer
              </Button>
            </Stack>
          )}
          <Box sx={{ display: { xs: 'none', lg: 'block' }, gridColumn: '1 / -1', width: '100%', height: '1px', bgcolor: 'divider', my: 0.25 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 36 }}>
            <TuneIcon fontSize="small" sx={{ color: '#f0fdf4' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1.2, color: '#f0fdf4' }}>Temps passé</Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={trackerFilter}
            onChange={(_, value) => value && setTrackerFilter(value)}
            sx={{ gridColumn: { xs: '1', lg: '2' }, flexWrap: 'nowrap', overflowX: 'auto' }}
          >
            <ToggleButton value="all">Tous</ToggleButton>
            <ToggleButton value="infogerance"><SupportAgentIcon sx={{ mr: 0.5, fontSize: 16 }} />Infogérance</ToggleButton>
            <ToggleButton value="commande"><ShoppingCartIcon sx={{ mr: 0.5, fontSize: 16 }} />Sur devis</ToggleButton>
            <ToggleButton value="autre"><MoreHorizIcon sx={{ mr: 0.5, fontSize: 16 }} />Autres</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      <Tabs
        value={activeTab}
        onChange={(_, value) => {
          setActiveTab(value);
        }}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ order: 2,
          mt: 2.5,
          mb: 1.25,
          minHeight: 52,
          px: 0.75,
          border: '1px solid rgba(52, 211, 153, 0.3)',
          borderRadius: 3,
          bgcolor: 'rgba(6, 50, 38, 0.94)',
          background: 'linear-gradient(115deg, rgba(255,255,255,.9) 0%, rgba(236,252,203,.78) 24%, rgba(209,250,229,.72) 52%, rgba(167,243,208,.58) 78%, rgba(220,252,231,.72) 100%)',
          boxShadow: '0 10px 28px rgba(5, 45, 32, 0.16)',
          backdropFilter: 'blur(14px)',
          position: 'relative', top: 16, zIndex: 1,
          '& .MuiTabs-flexContainer': { gap: 0.5 },
          '& .MuiTabs-indicator': {
            height: 4,
            borderRadius: '4px 4px 0 0',
            bgcolor: '#6ee7b7',
          },
        }}
      >
        <Tab value="contracts" icon={<DescriptionOutlinedIcon />} iconPosition="start" label="Activité globale" sx={{
          minHeight: 52,
          px: { xs: 1.5, sm: 2.5 },
          borderRadius: 1.5,
          color: '#14532d',
          fontWeight: 700,
          '&.Mui-selected': { color: '#064e3b', bgcolor: '#6ee7b7', fontWeight: 900, boxShadow: '0 5px 14px rgba(110,231,183,.38)', transform: 'translateY(-2px)' },
          '&:hover': { bgcolor: 'rgba(110,231,183,.28)', color: '#064e3b' },
        }} />
        <Tab value="analysis" icon={<QueryStatsOutlinedIcon />} iconPosition="start" label="Analyse du temps" sx={{
          minHeight: 52,
          px: { xs: 1.5, sm: 2.5 },
          borderRadius: 1.5,
          color: '#14532d',
          fontWeight: 700,
          '&.Mui-selected': { color: '#064e3b', bgcolor: '#6ee7b7', fontWeight: 900, boxShadow: '0 5px 14px rgba(110,231,183,.38)', transform: 'translateY(-2px)' },
          '&:hover': { bgcolor: 'rgba(110,231,183,.28)', color: '#064e3b' },
        }} />
        <Tab value="cloudtag" icon={<CloudOutlinedIcon />} iconPosition="start" label="CloudTag" sx={{
          minHeight: 52,
          px: { xs: 1.5, sm: 2.5 },
          borderRadius: 1.5,
          color: '#14532d',
          fontWeight: 700,
          '&.Mui-selected': { color: '#064e3b', bgcolor: '#6ee7b7', fontWeight: 900, boxShadow: '0 5px 14px rgba(110,231,183,.38)', transform: 'translateY(-2px)' },
          '&:hover': { bgcolor: 'rgba(110,231,183,.28)', color: '#064e3b' },
        }} />
        <Tab value="journal" icon={<HistoryOutlinedIcon />} iconPosition="start" label="Journal" sx={{
          minHeight: 52,
          px: { xs: 1.5, sm: 2.5 },
          borderRadius: 1.5,
          color: '#14532d',
          fontWeight: 700,
          '&.Mui-selected': { color: '#064e3b', bgcolor: '#6ee7b7', fontWeight: 900, boxShadow: '0 5px 14px rgba(110,231,183,.38)', transform: 'translateY(-2px)' },
          '&:hover': { bgcolor: 'rgba(110,231,183,.28)', color: '#064e3b' },
        }} />
        <Tab value="matrix" icon={<AccountTreeOutlinedIcon />} iconPosition="start" label="Matrice de compétence" sx={{
          minHeight: 52,
          px: { xs: 1.5, sm: 2.5 },
          borderRadius: 1.5,
          color: '#14532d',
          fontWeight: 700,
          '&.Mui-selected': { color: '#064e3b', bgcolor: '#6ee7b7', fontWeight: 900, boxShadow: '0 5px 14px rgba(110,231,183,.38)', transform: 'translateY(-2px)' },
          '&:hover': { bgcolor: 'rgba(110,231,183,.28)', color: '#064e3b' },
        }} />
      </Tabs>

      {selectedContract && (
        <Typography variant="h6" sx={{ order: 4, fontWeight: 900, mt: 1, mb: 0.5, px: 0.5 }}>
          {activePeriodLabel}
        </Typography>
      )}

      {!selectedContract ? (
        <Alert severity="info" sx={{ mt: 8, mb: 2, borderRadius: 2, alignItems: 'center' }}>Sélectionnez un contrat pour afficher les données de cet onglet.</Alert>
      ) : activeTab === 'journal' ? (
        <Box sx={{ order: 5 }}><ClientActivityJournal
          embedded
          filters={{
            contractId: selectedContractId || undefined,
            periodMode: selectedPeriod,
            year: selectedYear,
            startDate: selectedPeriod === 'custom' ? customStartDate : (contractPeriod ? toDateInputValue(contractPeriod.start) : undefined),
            endDate: selectedPeriod === 'custom' ? customEndDate : (contractPeriod ? toDateInputValue(contractPeriod.end) : undefined),
          }}
        /></Box>
      ) : activeTab === 'matrix' ? (
        <Paper variant="outlined" sx={{ order: 5, mt: 1.5, borderRadius: 2, overflow: 'hidden', boxShadow: 2 }}>
          <Box sx={{ px: 2.5, pt: 2.25, pb: 1.75 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Matrice de compétence</Typography>
                <Typography variant="body2" color="text.secondary">
                  Vérifiez les compétences comprises dans le contrat sélectionné.
                </Typography>
              </Box>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={selectedContract.matrix?.name || 'Matrice du contrat'}
              />
            </Stack>
          </Box>
          <TableContainer sx={{ maxHeight: 680 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900 }}>Compétence 1</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Niveau</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 900 }}>Admin</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 900 }}>Ingé</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 900 }}>Pilotage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matrixRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{row.competence1}</TableCell>
                    <TableCell>{row.competence2}</TableCell>
                    {['admin', 'ingenieur', 'pilotage'].map((profile) => (
                      <TableCell key={profile} align="center">
                        {row.includedProfiles[profile] ? (
                          <CheckCircleOutlineIcon color="success" fontSize="small" titleAccess="Inclus" />
                        ) : (
                          <CancelOutlinedIcon color="disabled" fontSize="small" titleAccess="Non inclus" />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {!matrixRows.length && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      Aucune compétence rattachée à ce contrat.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : activeTab === 'contracts' ? <>
      {selectedContract ? <Box sx={{ order: 5 }}>
        <Grid container spacing={2.5}>
          <Grid item size={{ xs: 12, md: 3 }}>
            <ContractMetricCard
              label="Admin"
              helper="Heures administrateur prévues"
              icon={<AdminPanelSettingsIcon />}
              data={periodData?.[activePeriodKey]}
              metricKey="admin"
              color="#0f766e"
              contract={selectedContract}
              selected={consumptionFilter === 'admin'}
              onClick={() => setConsumptionFilter((current) => current === 'admin' ? 'all' : 'admin')}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 3 }}>
            <ContractMetricCard
              label="Ingé"
              helper="Heures ingénieur prévues"
              icon={<EngineeringIcon />}
              data={periodData?.[activePeriodKey]}
              metricKey="engineer"
              color="#2563eb"
              contract={selectedContract}
              selected={consumptionFilter === 'engineer'}
              onClick={() => setConsumptionFilter((current) => current === 'engineer' ? 'all' : 'engineer')}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 3 }}>
            <ContractMetricCard
              label="Pilotage"
              helper="Heures pilotage prévues"
              icon={<ManageAccountsIcon />}
              data={periodData?.[activePeriodKey]}
              metricKey="pilotage"
              color="#7c3aed"
              contract={selectedContract}
              selected={consumptionFilter === 'pilotage'}
              onClick={() => setConsumptionFilter((current) => current === 'pilotage' ? 'all' : 'pilotage')}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 3 }}>
            <TravelMetricCard
              data={periodData?.[activePeriodKey]}
              contract={selectedContract}
              selected={consumptionFilter === 'travel'}
              onClick={() => setConsumptionFilter((current) => current === 'travel' ? 'all' : 'travel')}
            />
          </Grid>
        </Grid>
      </Box> : (
        <Alert severity="info" sx={{ mt: 1 }}>
          Sélectionnez un contrat ci-dessous pour afficher les heures consommées, les déplacements et les détails associés.
        </Alert>
      )}

      {selectedContract && false && (
        <Box sx={{ order: 5, px: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1} sx={{ width: '100%', pr: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>{selectedContract.name || selectedContract.reference || 'Contrat'}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedContract.reference || 'Contrat de service'}</Typography>
              </Box>
              <Chip size="small" color="primary" variant="outlined" label={`${formatCurrency(getContractArticlesMonthlyTotal(selectedContract))} / mois`} />
            </Stack>
        </Box>
      )}

      {selectedContract && false && (
        <Paper variant="outlined" sx={{ order: 5, p: 1.5, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={800}>Actions contrats</Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => setViewingContract(selectedContract)}>
                Voir le contrat
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} disabled>
                Télécharger
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Dialog open={!!viewingContract} onClose={() => setViewingContract(null)} fullWidth maxWidth="md">
        <DialogTitle>{`Contrat - ${viewingContract?.name || viewingContract?.reference || ''}`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={viewingContract?.reference || 'Sans référence'} />
              <Chip label={`${formatCurrency(getContractArticlesMonthlyTotal(viewingContract))} / mois`} color="primary" variant="outlined" />
              <Chip label={`${formatCurrency(getContractArticlesAnnualTotal(viewingContract))} / an`} variant="outlined" />
              <Chip label={viewingContract?.hasArticleHours ? 'Articles contrat' : 'Champs contrat'} />
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item size={{ xs: 12, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Admin inclus</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{formatHours(viewingContract?.[activeIncludedKey]?.admin)}</Typography>
                </Paper>
              </Grid>
              <Grid item size={{ xs: 12, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Ingé inclus</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{formatHours(viewingContract?.[activeIncludedKey]?.engineer)}</Typography>
                </Paper>
              </Grid>
              <Grid item size={{ xs: 12, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Pilotage inclus</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{formatHours(viewingContract?.[activeIncludedKey]?.pilotage)}</Typography>
                </Paper>
              </Grid>
              <Grid item size={{ xs: 12, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Déplacements inclus</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{Number(viewingContract?.[activeIncludedKey]?.travel || 0).toLocaleString('fr-FR')}</Typography>
                </Paper>
              </Grid>
            </Grid>
            {(() => {
              const articles = viewingContract?.articles || [];
              const infogeranceArticles = articles.filter(isInfogeranceArticle);
              const otherArticles = articles.filter((article) => !isInfogeranceArticle(article));
              return (
                <Stack spacing={2}>
                  <ContractArticlesSection title="Infogérance" articles={infogeranceArticles} />
                  <ContractArticlesSection title="Autres articles" articles={otherArticles} />
                </Stack>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingContract(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {selectedContract && false && (
        <Paper variant="outlined" sx={{ order: 5, p: 2.25, borderRadius: 2, mt: 1.5, boxShadow: 2 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Régularisation financière</Typography>
              <Typography variant="body2" color="text.secondary">Écart valorisé selon les prix unitaires des articles du CRT sélectionné.</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Élément</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Écart quantité</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Tarif horaire / unité</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Régularisation</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {[
                    ['Heures admin', 'admin', 'admin_hours'],
                    ['Heures ingé', 'engineer', 'engineer_hours'],
                    ['Pilotage', 'pilotage', 'pilotage_days'],
                    ['Déplacements', 'travel', 'travel'],
                  ].map(([label, key, metric]) => {
                    const metricData = periodData?.[activePeriodKey];
                    const delta = Number(metricData?.delta?.[key] || 0);
                    const rate = getContractMetricRate(selectedContract, metric);
                    const amount = getContractFinancialDelta(selectedContract, metricData, key);
                    return <TableRow key={key} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{label}</TableCell>
                      <TableCell align="right">{key === 'travel' ? formatCount(delta) : formatHours(delta)}</TableCell>
                      <TableCell align="right">{formatCurrency(rate)}{key === 'travel' ? ' / déplacement' : ' / h'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: amount > 0 ? 'error.main' : 'success.main' }}>{formatSignedCurrency(amount)}</TableCell>
                    </TableRow>;
                  })}
                  <TableRow>
                    <TableCell colSpan={3} sx={{ fontWeight: 900 }}>Total régularisation</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>{formatSignedCurrency(['admin', 'engineer', 'pilotage', 'travel'].reduce((sum, key) => sum + getContractFinancialDelta(selectedContract, periodData?.[activePeriodKey], key), 0))}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      )}

      {selectedContract && (
        <Paper variant="outlined" sx={{ order: 5, mt: 1.5, borderRadius: 2, overflow: 'hidden', boxShadow: 2, bgcolor: 'background.paper' }}>
          <Box sx={{ px: 2.5, pt: 2.25, pb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Vos consommations</Typography>
          </Box>

          <Box sx={{ px: 2.5, py: 2.25, borderTop: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#37305a', fontWeight: 800 }}>Filtres</Typography>
            <Grid container spacing={1.75}>
              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Intervenant, ticket ou commentaire"
                  placeholder="Rechercher"
                  value={consumptionSearch}
                  onChange={(event) => setConsumptionSearch(event.target.value)}
                  slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment> } }}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="consumption-tracker-filter-label">Tracker</InputLabel>
                  <Select
                    labelId="consumption-tracker-filter-label"
                    value={tableTrackerFilter}
                    label="Tracker"
                    onChange={(event) => setTableTrackerFilter(event.target.value)}
                  >
                    <MenuItem value="all">Tous les trackers</MenuItem>
                    {trackerOptions.map((tracker) => <MenuItem key={tracker} value={tracker}>{tracker}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="consumption-profile-filter-label">Niveau</InputLabel>
                  <Select
                    labelId="consumption-profile-filter-label"
                    value={tableProfileFilter}
                    label="Niveau"
                    onChange={(event) => setTableProfileFilter(event.target.value)}
                  >
                    <MenuItem value="all">Tous les niveaux</MenuItem>
                    {profileOptions.map((profile) => (
                      <MenuItem key={profile} value={profile}>
                        {{ admin: 'Admin', engineer: 'Ingé', pilotage: 'Pilotage' }[profile] || profile}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">{consumptionRows.length} ligne(s)</Typography>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setConsumptionSearch('');
                setTableTrackerFilter('all');
                setTableProfileFilter('all');
              }}
            >
              Réinitialiser
            </Button>
          </Box>

          <Box sx={{ px: 1.25, pb: 1.25 }}>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1100 }}>
                <TableHead><TableRow sx={{ bgcolor: 'action.hover' }}>
                  {['Ticket', 'Intervenant', 'Tracker', 'Heures', 'Déplacement', 'Commentaire'].map((label) => <TableCell key={label} sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{label}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {groupedConsumptionRows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>Aucune consommation sur cette période.</TableCell></TableRow>}
                  {groupedConsumptionRows.map((group) => {
                    const expanded = expandedConsumptionTickets.has(group.ticket);
                    const first = group.rows[0] || {};
                    const assigneeInitials = getInitials(first.ticketAssignedTo);
                    return <Fragment key={group.ticket}>
                      <TableRow key={`${group.ticket}-summary`} hover onClick={() => setExpandedConsumptionTickets((previous) => { const next = new Set(previous); if (next.has(group.ticket)) next.delete(group.ticket); else next.add(group.ticket); return next; })} sx={{ cursor: 'pointer', '& > td': { borderBottom: expanded ? 0 : undefined } }}>
                        <TableCell><Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} /><Chip size="small" color="primary" variant="outlined" label={group.ticket === 'Sans ticket' ? group.ticket : `#${group.ticket}`} /><Typography variant="body2" sx={{ fontWeight: 700 }}>{first.ticketSubject || 'Consommation'}</Typography></Stack></TableCell>
                        <TableCell><Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: 'success.main' }}>{assigneeInitials}</Avatar><Typography variant="body2">{first.ticketAssignedTo || '-'}</Typography></Stack></TableCell>
                        <TableCell>{first.tracker || '-'}</TableCell>
                        <TableCell><Chip size="small" color="success" label={`${formatHours(group.total)} · ${group.rows.length}`} /></TableCell>
                        <TableCell>{group.rows.some((row) => Number(row.travel || row.displacement || 0) > 0) ? <Chip size="small" color="warning" icon={<CommuteIcon />} label="Oui" /> : '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 360 }}>{first.comments || '-'}</TableCell>
                      </TableRow>
                      {expanded && (() => {
                        const timeEvents = group.rows.map((row, index) => ({
                          type: 'entry',
                          row,
                          value: row.spentOn,
                          order: index,
                        })).sort((left, right) => {
                          const leftTime = left.value ? new Date(left.value).getTime() : Number.POSITIVE_INFINITY;
                          const rightTime = right.value ? new Date(right.value).getTime() : Number.POSITIVE_INFINITY;
                          return (leftTime - rightTime) || (left.order - right.order);
                        });
                        const detailEvents = [
                          { type: 'created', value: first.ticketCreatedAt, order: -1 },
                          ...timeEvents,
                          { type: 'closed', value: first.ticketClosedAt },
                          { type: 'due', value: first.ticketDueDate, order: 1 },
                        ]
                          .filter((event) => event.type !== 'closed' || event.value)
                          .sort((left, right) => {
                            if (left.type === 'created') return -1;
                            if (right.type === 'created') return 1;
                            if (left.type === 'due') return 1;
                            if (right.type === 'due') return -1;
                            const leftTime = left.value ? new Date(left.value).getTime() : Number.POSITIVE_INFINITY;
                            const rightTime = right.value ? new Date(right.value).getTime() : Number.POSITIVE_INFINITY;
                            return (leftTime - rightTime) || ((left.order || 0) - (right.order || 0));
                          });

                        return <TableRow key={`${group.ticket}-details`}><TableCell colSpan={6} sx={{ p: 0, bgcolor: 'background.default' }}>
                          <Table size="small" sx={{ minWidth: 980 }}>
                            <TableHead><TableRow sx={{ bgcolor: 'action.hover' }}>
                              {['Date', 'Intervenant', 'Facturable', 'Activité', 'Niveau', 'Heures', 'Déplacement', 'Commentaire'].map((label) => <TableCell key={label} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</TableCell>)}
                            </TableRow></TableHead>
                            <TableBody>{detailEvents.map((event, index) => {
                              if (event.type === 'created') return <TableRow key={`${group.ticket}-created`}><TableCell colSpan={8} sx={{ py: 1, bgcolor: 'rgba(34,197,94,.12)', color: 'text.primary' }}><Typography variant="caption" sx={{ fontWeight: 800, mr: 1, color: 'text.primary' }}>Création du ticket</Typography>{formatDateTime(event.value)}</TableCell></TableRow>;
                              if (event.type === 'closed') return <TableRow key={`${group.ticket}-closed`}><TableCell colSpan={8} sx={{ py: 1, bgcolor: 'rgba(59,130,246,.12)', color: 'text.primary' }}><Typography variant="caption" sx={{ fontWeight: 800, mr: 1, color: 'text.primary' }}>Clôture du ticket</Typography>{formatDateTime(event.value)}</TableCell></TableRow>;
                              if (event.type === 'due') return <TableRow key={`${group.ticket}-due`}><TableCell colSpan={8} sx={{ py: 1, bgcolor: 'rgba(245,158,11,.14)', color: 'text.primary' }}><Typography variant="caption" sx={{ fontWeight: 800, mr: 1, color: 'text.primary' }}>Échéance du ticket</Typography>{formatDateOnly(event.value)}</TableCell></TableRow>;
                              const row = event.row;
                              const user = row.user || '-';
                              const normalizedProfile = normalizeProfile(row.profile);
                              const competence1 = row.competence1 || row.competence || '-';
                              const competence2 = row.competence2 || '-';
                              const levelChip = normalizedProfile && normalizedProfile !== 'unassigned' ? (
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title={<Stack spacing={0.25} sx={{ p: 0.5 }}><Typography variant="caption"><strong>Compétence 1 :</strong> {competence1}</Typography><Typography variant="caption"><strong>Compétence 2 :</strong> {competence2}</Typography></Stack>}
                                >
                                  <Chip size="small" variant="outlined" color="success" icon={profileIcon(normalizedProfile)} label={profileLabel(normalizedProfile)} />
                                </Tooltip>
                              ) : <Chip size="small" variant="outlined" label="Non affecté" />;
                              return <TableRow key={`${row.id || 'entry'}-${index}`} hover>
                                <TableCell sx={{ width: 150, whiteSpace: 'nowrap' }}>{formatDateOnly(row.spentOn)}</TableCell>
                                <TableCell sx={{ width: 180 }}><Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: 'success.main' }}>{getInitials(user)}</Avatar><Typography variant="body2">{user}</Typography></Stack></TableCell>
                                <TableCell sx={{ minWidth: 150 }}>{row.facturable || '-'}</TableCell>
                                <TableCell sx={{ minWidth: 160 }}>{row.activity || '-'}</TableCell>
                                <TableCell sx={{ minWidth: 130 }}>{levelChip}</TableCell>
                                <TableCell align="right"><Chip size="small" color="success" label={formatHours(row.hours)} /></TableCell>
                                <TableCell>{Number(row.travel || row.displacement || 0) > 0 ? <Chip size="small" color="warning" icon={<CommuteIcon />} label="Oui" /> : '-'}</TableCell>
                                <TableCell sx={{ maxWidth: 360 }}>{row.comments || '-'}</TableCell>
                              </TableRow>;
                            })}</TableBody>
                          </Table>
                        </TableCell></TableRow>;
                      })()}
                    </Fragment>;
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      )}
      </>
      : activeTab === 'cloudtag' ? (
        <Box sx={{ order: 5 }}><ClientTimeAnalytics
          embedded
          cloudTagOnly
          sharedFilters={{
            contractId: selectedContractId || 'all',
            trackerFilter,
            periodMode: selectedPeriod,
            selectedYear,
            customStartDate,
            customEndDate,
          }}
        /></Box>
      ) : (
        <>
          <Box sx={{ order: 5 }}><ClientTimeAnalytics
            embedded
            sharedFilters={{
              contractId: selectedContractId || 'all',
              setContractId: setSelectedContractId,
              trackerFilter,
              setTrackerFilter,
              periodMode: selectedPeriod,
              setPeriodMode: setSelectedPeriod,
              selectedYear,
              setSelectedYear,
              customStartDate,
              setCustomStartDate,
              customEndDate,
              setCustomEndDate,
            }}
          /></Box>
        </>
      )}
    </Stack>
    </>
  );
}
